import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { UserService } from './user.service';

export type SupportedLocale = 'de' | 'en';

@Injectable({
  providedIn: 'root',
})
export class LocaleSwitchService {
  constructor(
    @Inject(LOCALE_ID) private readonly localeId: string,
    private readonly userService: UserService
  ) {}

  toggleLanguage(userId?: string): void {
    const nextLocale: SupportedLocale =
      this.getActiveLocale() === 'de' ? 'en' : 'de';
    this.persistLanguage(nextLocale, userId);
    this.redirectTo(nextLocale);
  }

  switchToLanguage(lang: SupportedLocale, userId?: string): void {
    this.persistLanguage(lang, userId);
    this.redirectTo(lang);
  }

  private persistLanguage(lang: SupportedLocale, userId?: string): void {
    if (!userId) return;
    this.userService.updateUserPartial(userId, { preferredLanguage: lang });
  }

  private redirectTo(locale: SupportedLocale): void {
    const { pathname, search, hash } = window.location;
    const activeLocalePrefix = this.getActiveLocaleFromPath(pathname);

    const pathWithoutLocale = activeLocalePrefix
      ? pathname.replace(new RegExp(`^/${activeLocalePrefix}(?=/|$)`), '')
      : pathname;

    const normalizedPath = pathWithoutLocale.startsWith('/')
      ? pathWithoutLocale
      : `/${pathWithoutLocale}`;

    window.location.assign(`/${locale}${normalizedPath}${search}${hash}`);
  }

  getActiveLocale(): SupportedLocale {
    return (
      this.getActiveLocaleFromPath(window.location.pathname) ??
      this.getLocaleFromLocaleId()
    );
  }

  private getLocaleFromLocaleId(): SupportedLocale {
    const locale = this.localeId.toLowerCase();
    return locale.startsWith('de') ? 'de' : 'en';
  }

  private getActiveLocaleFromPath(
    pathname: string
  ): SupportedLocale | undefined {
    if (pathname === '/de' || pathname.startsWith('/de/')) {
      return 'de';
    }
    if (pathname === '/en' || pathname.startsWith('/en/')) {
      return 'en';
    }
    return undefined;
  }
}
